import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtMoneyCur, CREDITO_ESTATUS } from "../lib/format";
import { EST_BADGE } from "./Creditos";
import { Card, SectionTitle } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, FileDown, Sparkles } from "lucide-react";

const CP_EST = ["Pendiente", "Entregada", "Aceptada por el banco"];
const COV_EST = ["Cumplido", "En riesgo", "Incumplido"];
const DISP_EST = ["Programada", "Solicitada", "Ministrada", "Rechazada"];

export default function CreditoDetail() {
  const { pid, cid } = useParams();
  const { user, isAdmin } = useAuth();
  const canFin = user && (user.role === "admin" || user.role === "financiero");
  const [cr, setCr] = useState(null);
  const [tramites, setTramites] = useState([]);
  const [tab, setTab] = useState("cps");
  const [estModal, setEstModal] = useState(null);
  const [dispModal, setDispModal] = useState(null);
  const [pagoModal, setPagoModal] = useState(null);

  const load = () => api.get(`/creditos/${cid}`).then((r) => setCr(r.data));
  useEffect(() => { load(); api.get(`/properties/${pid}/tramites`).then((r) => setTramites(r.data)); }, [cid]); // eslint-disable-line

  const patch = async (changes, msg = "Crédito actualizado") => {
    try { const { data } = await api.patch(`/creditos/${cid}`, changes); setCr(data); toast.success(msg); }
    catch (e) { toast.error(apiError(e)); }
  };

  const cambiarEstatus = async () => {
    try { await api.post(`/creditos/${cid}/estatus`, estModal); toast.success("Estatus actualizado"); setEstModal(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const guardarDisp = async (override = false) => {
    try {
      await api.post(`/creditos/${cid}/disposiciones`, { ...dispModal, monto: Number(dispModal.monto) || 0, override });
      toast.success("Disposición registrada"); setDispModal(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.codigo === "cps_pendientes") {
        if (isAdmin && window.confirm(`CPs pendientes:\n- ${d.pendientes.join("\n- ")}\n\n¿Aplicar OVERRIDE de administrador? (quedará auditado)`)) return guardarDisp(true);
        toast.error("Hay condiciones precedentes sin aceptar por el banco");
      } else toast.error(apiError(e));
    }
  };

  const generarAmort = async () => {
    try { await api.post(`/creditos/${cid}/amortizacion/generar`, { base: cr.monto_dispuesto > 0 ? "dispuesto" : "autorizado" }); toast.success("Tabla de amortización generada"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const registrarPago = async () => {
    try {
      await api.post(`/creditos/${cid}/pagos`, { fecha: pagoModal.fecha, monto_capital: Number(pagoModal.capital) || 0, monto_interes: Number(pagoModal.interes) || 0, nota: pagoModal.nota || "" });
      toast.success("Pago registrado"); setPagoModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pdf = async () => {
    const r = await api.get(`/creditos/${cid}/reporte-pdf`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `reporte_acreditante.pdf`; a.click();
  };

  if (!cr) return <div className="p-10 text-slate-400 animate-pulse">Cargando expediente...</div>;
  const rowL = (arr, i, k, v) => { const c = [...arr]; c[i] = { ...c[i], [k]: v }; return c; };

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <Link to={`/p/${pid}/creditos`} className="text-xs text-[#00A3B5] font-bold uppercase tracking-wider flex items-center gap-1 mb-2"><ArrowLeft size={12} /> Créditos</Link>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Expediente de crédito</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#002B49]">{cr.acreditante_nombre || "Sin acreditante"} · {cr.tipo}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canFin ? (
            <Select value={cr.estatus} onValueChange={(v) => setEstModal({ estatus: v, comentario: "" })}>
              <SelectTrigger data-testid="credito-estatus-select" className="h-9 w-60 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{CREDITO_ESTATUS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          ) : <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${EST_BADGE[cr.estatus]}`}>{cr.estatus}</span>}
          <Button data-testid="reporte-acreditante-button" variant="outline" onClick={pdf} className="h-9 gap-2"><FileDown size={14} /> Reporte al acreditante</Button>
        </div>
      </div>
      {cr.estatus === "En incumplimiento" && <p className="text-sm font-bold text-white bg-red-600 rounded-md px-4 py-2.5 mb-4">⚠ CRÉDITO EN INCUMPLIMIENTO</p>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[["Autorizado", fmtMoneyCur(cr.monto_autorizado, cr.moneda)], ["Dispuesto", fmtMoneyCur(cr.monto_dispuesto, cr.moneda)],
          ["Disponible", fmtMoneyCur(cr.disponible, cr.moneda)], ["Saldo insoluto", fmtMoneyCur(cr.saldo_insoluto, cr.moneda)],
          ["Tasa anual", `${cr.tasa_anual?.toFixed(2)}%`]].map(([l, v]) => (
          <Card key={l} className="p-3 text-center">
            <p className="font-display font-bold text-[#002B49]">{v}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{l}</p>
          </Card>))}
      </div>

      <Card className="p-5 mb-5">
        <SectionTitle>Términos del crédito</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {[["monto_autorizado", "Monto autorizado", "number"], ["plazo_meses", "Plazo (meses)", "number"],
            ["gracia_meses", "Gracia (meses)", "number"], ["fecha_firma", "Fecha de firma", "date"],
            ["fecha_vencimiento", "Vencimiento", "date"], ["tasa_fija", "Tasa fija %", "number"],
            ["tasa_ref_valor", "Tasa referencia %", "number"], ["spread", "Spread %", "number"]].map(([k, l, tp]) => (
            <div key={k} className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
              <Input data-testid={`credito-${k}-input`} disabled={!canFin} type={tp} className="h-8 text-sm" defaultValue={cr[k] ?? ""}
                onBlur={(e) => { const v = tp === "number" ? Number(e.target.value) || 0 : e.target.value; if (v !== cr[k] && (v || cr[k])) patch({ [k]: v }); }} />
            </div>))}
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Tipo de tasa</p>
            <Select disabled={!canFin} value={cr.tasa_tipo} onValueChange={(v) => patch({ tasa_tipo: v })}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="fija">Fija</SelectItem><SelectItem value="variable">Variable (ref + spread)</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Esquema de pago</p>
            <Select disabled={!canFin} value={cr.esquema} onValueChange={(v) => patch({ esquema: v })}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="iguales">Pagos iguales</SelectItem><SelectItem value="lineal">Capital lineal</SelectItem><SelectItem value="bullet">Bullet</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1 col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Destino / garantías (texto libre)</p>
            <Input disabled={!canFin} className="h-8 text-sm" defaultValue={cr.destino || ""} onBlur={(e) => e.target.value !== cr.destino && patch({ destino: e.target.value })} />
          </div>
        </div>
      </Card>

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {[["cps", `Condiciones precedentes (${cr.cps_aceptadas}/${cr.cps_total})`], ["disposiciones", `Disposiciones (${(cr.disposiciones || []).length})`],
          ["amortizacion", "Servicio de deuda"], ["covenants", `Covenants (${(cr.covenants || []).length})`]].map(([k, l]) => (
          <button key={k} data-testid={`credito-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-[#00A3B5] text-[#002B49]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{l}</button>))}
      </div>

      {tab === "cps" && (
        <Card className="p-5">
          <SectionTitle right={canFin && <Button data-testid="cp-agregar" variant="outline" className="h-8 gap-1 text-xs"
            onClick={() => patch({ cps: [...(cr.cps || []), { descripcion: "Nueva condición", responsable: "", estatus: "Pendiente", tramite_id: null }] })}><Plus size={12} /> CP</Button>}>Checklist de condiciones precedentes</SectionTitle>
          <div className="space-y-2">
            {(cr.cps || []).length === 0 && <p className="text-sm text-slate-400">Sin CPs. El expediente de permisos es tu data room para el banco: vincula cada CP a su trámite.</p>}
            {(cr.cps || []).map((cp, i) => (
              <div key={cp.id || i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-slate-200 rounded-md p-2.5" data-testid={`cp-row-${i}`}>
                <Input disabled={!canFin} className="h-8 text-xs md:col-span-4" value={cp.descripcion} onChange={(e) => patch({ cps: rowL(cr.cps, i, "descripcion", e.target.value) })} />
                <Input disabled={!canFin} className="h-8 text-xs md:col-span-2" placeholder="Responsable" value={cp.responsable || ""} onChange={(e) => patch({ cps: rowL(cr.cps, i, "responsable", e.target.value) })} />
                <div className="md:col-span-3">
                  <Select disabled={!canFin} value={cp.tramite_id || "ninguno"} onValueChange={(v) => patch({ cps: rowL(cr.cps, i, "tramite_id", v === "ninguno" ? null : v) })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Vincular trámite" /></SelectTrigger>
                    <SelectContent><SelectItem value="ninguno">Sin trámite vinculado</SelectItem>
                      {tramites.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre.slice(0, 45)}</SelectItem>)}</SelectContent>
                  </Select>
                  {cp.candidata_entregada && <p className="text-[10px] text-emerald-600 font-bold mt-0.5 flex items-center gap-1"><Sparkles size={10} /> Trámite aprobado — candidata a Entregada</p>}
                  {cp.tramite_nombre && !cp.candidata_entregada && <p className="text-[10px] text-slate-400 mt-0.5">{cp.tramite_nombre}: {cp.tramite_estatus}</p>}
                </div>
                <div className="md:col-span-2">
                  <Select disabled={!canFin} value={cp.estatus} onValueChange={(v) => patch({ cps: rowL(cr.cps, i, "estatus", v) })}>
                    <SelectTrigger data-testid={`cp-estatus-${i}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{CP_EST.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="md:col-span-1 text-right">{canFin && <button onClick={() => patch({ cps: cr.cps.filter((_, j) => j !== i) })} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}</div>
              </div>))}
          </div>
        </Card>)}

      {tab === "disposiciones" && (
        <Card className="p-5">
          <SectionTitle right={canFin && <Button data-testid="disp-agregar" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setDispModal({ estatus: "Ministrada" })}><Plus size={12} /> Disposición</Button>}>Ministraciones / disposiciones</SectionTitle>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="py-2">#</th><th className="py-2">Solicitada</th><th className="py-2">Ministrada</th>
              <th className="py-2 text-right">Monto</th><th className="py-2 text-right">Avance req./cert.</th><th className="py-2">Estatus</th></tr></thead>
            <tbody>
              {(cr.disposiciones || []).length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400">Sin disposiciones registradas.</td></tr>}
              {(cr.disposiciones || []).map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="py-2 font-bold text-[#002B49]">#{d.numero}</td>
                  <td className="py-2 text-slate-600">{d.fecha_solicitada ? fmtDate(d.fecha_solicitada) : "—"}</td>
                  <td className="py-2 text-slate-600">{d.fecha_ministrada ? fmtDate(d.fecha_ministrada) : "—"}</td>
                  <td className="py-2 text-right font-semibold">{fmtMoneyCur(d.monto, cr.moneda)}</td>
                  <td className="py-2 text-right text-slate-600">{d.avance_requerido || 0}% / {d.avance_certificado || 0}%</td>
                  <td className="py-2">{d.estatus}</td>
                </tr>))}
            </tbody>
          </table>
        </Card>)}

      {tab === "amortizacion" && (
        <Card className="p-5">
          <SectionTitle right={canFin && <div className="flex gap-2">
            <Button data-testid="generar-amortizacion-button" variant="outline" className="h-8 gap-1 text-xs" onClick={generarAmort}><Sparkles size={12} /> Generar tabla</Button>
            <Button data-testid="registrar-pago-button" className="h-8 gap-1 text-xs bg-[#00A3B5] text-white" onClick={() => setPagoModal({ fecha: new Date().toISOString().slice(0, 10) })}><Plus size={12} /> Registrar pago</Button>
          </div>}>Tabla de amortización {cr.proximo_pago && <span className="text-xs font-normal text-slate-500">· próximo pago {fmtDate(cr.proximo_pago.fecha)} por {fmtMoneyCur(cr.proximo_pago.pago, cr.moneda)}</span>}</SectionTitle>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="tabla-amortizacion">
              <thead className="sticky top-0 bg-white"><tr className="text-left uppercase tracking-wider text-slate-500">
                <th className="py-2">#</th><th className="py-2">Fecha</th><th className="py-2 text-right">Capital</th>
                <th className="py-2 text-right">Interés</th><th className="py-2 text-right">Pago</th><th className="py-2 text-right">Saldo</th></tr></thead>
              <tbody>
                {(cr.amortizacion || []).length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400">Genera la tabla a partir del monto, tasa, plazo, gracia y esquema.</td></tr>}
                {(cr.amortizacion || []).map((f) => (
                  <tr key={f.numero} className="border-t border-slate-100">
                    <td className="py-1.5">{f.numero}</td><td className="py-1.5">{fmtDate(f.fecha)}</td>
                    <td className="py-1.5 text-right">{fmtMoneyCur(f.capital, cr.moneda)}</td>
                    <td className="py-1.5 text-right">{fmtMoneyCur(f.interes, cr.moneda)}</td>
                    <td className="py-1.5 text-right font-semibold">{fmtMoneyCur(f.pago, cr.moneda)}</td>
                    <td className="py-1.5 text-right text-slate-500">{fmtMoneyCur(f.saldo, cr.moneda)}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
          {(cr.pagos_realizados || []).length > 0 && <>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-4 mb-2">Pagos realizados</p>
            {(cr.pagos_realizados || []).map((pg) => (
              <p key={pg.id} className="text-xs text-slate-600">{fmtDate(pg.fecha)} · capital {fmtMoneyCur(pg.monto_capital, cr.moneda)} + interés {fmtMoneyCur(pg.monto_interes, cr.moneda)} {pg.nota && `· ${pg.nota}`}</p>))}
          </>}
        </Card>)}

      {tab === "covenants" && (
        <Card className="p-5">
          <SectionTitle right={canFin && <Button data-testid="covenant-agregar" variant="outline" className="h-8 gap-1 text-xs"
            onClick={() => patch({ covenants: [...(cr.covenants || []), { tipo: "obligación de información", descripcion: "Nuevo covenant", periodicidad: "trimestral", proximo_vencimiento: "", responsable: "", estatus: "Cumplido" }] })}><Plus size={12} /> Covenant</Button>}>Covenants y obligaciones</SectionTitle>
          <div className="space-y-2">
            {(cr.covenants || []).length === 0 && <p className="text-sm text-slate-400">Sin covenants registrados (razones financieras, obligaciones de hacer/no hacer, información periódica).</p>}
            {(cr.covenants || []).map((cov, i) => (
              <div key={cov.id || i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-slate-200 rounded-md p-2.5" data-testid={`covenant-row-${i}`}>
                <Input disabled={!canFin} className="h-8 text-xs md:col-span-5" value={cov.descripcion} onChange={(e) => patch({ covenants: rowL(cr.covenants, i, "descripcion", e.target.value) })} />
                <Input disabled={!canFin} className="h-8 text-xs md:col-span-2" placeholder="Responsable" value={cov.responsable || ""} onChange={(e) => patch({ covenants: rowL(cr.covenants, i, "responsable", e.target.value) })} />
                <Input disabled={!canFin} className="h-8 text-xs md:col-span-2" type="date" value={cov.proximo_vencimiento || ""} onChange={(e) => patch({ covenants: rowL(cr.covenants, i, "proximo_vencimiento", e.target.value) })} />
                <div className="md:col-span-2">
                  <Select disabled={!canFin} value={cov.estatus} onValueChange={(v) => patch({ covenants: rowL(cr.covenants, i, "estatus", v) })}>
                    <SelectTrigger data-testid={`covenant-estatus-${i}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{COV_EST.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="md:col-span-1 text-right">{canFin && <button onClick={() => patch({ covenants: cr.covenants.filter((_, j) => j !== i) })} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}</div>
              </div>))}
          </div>
        </Card>)}

      <Dialog open={!!estModal} onOpenChange={(v) => !v && setEstModal(null)}>
        <DialogContent className="max-w-sm" data-testid="credito-estatus-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Cambiar estatus del crédito</DialogTitle></DialogHeader>
          {estModal && <>
            <p className="text-sm text-slate-600">{cr.estatus} → <b>{estModal.estatus}</b></p>
            <Textarea data-testid="credito-estatus-comentario" rows={2} placeholder="Comentario obligatorio (auditoría)" value={estModal.comentario} onChange={(e) => setEstModal({ ...estModal, comentario: e.target.value })} />
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEstModal(null)}>Cancelar</Button>
              <Button data-testid="credito-estatus-confirmar" onClick={cambiarEstatus} disabled={!estModal.comentario.trim()} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Confirmar</Button></div>
          </>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dispModal} onOpenChange={(v) => !v && setDispModal(null)}>
        <DialogContent className="max-w-sm" data-testid="disposicion-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Registrar disposición</DialogTitle></DialogHeader>
          {dispModal && <div className="space-y-3">
            <Input data-testid="disp-monto-input" type="number" placeholder="Monto *" value={dispModal.monto || ""} onChange={(e) => setDispModal({ ...dispModal, monto: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" value={dispModal.fecha_solicitada || ""} onChange={(e) => setDispModal({ ...dispModal, fecha_solicitada: e.target.value })} />
              <Input type="date" value={dispModal.fecha_ministrada || ""} onChange={(e) => setDispModal({ ...dispModal, fecha_ministrada: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Avance requerido %" value={dispModal.avance_requerido || ""} onChange={(e) => setDispModal({ ...dispModal, avance_requerido: Number(e.target.value) })} />
              <Input type="number" placeholder="Avance certificado %" value={dispModal.avance_certificado || ""} onChange={(e) => setDispModal({ ...dispModal, avance_certificado: Number(e.target.value) })} />
            </div>
            <Select value={dispModal.estatus} onValueChange={(v) => setDispModal({ ...dispModal, estatus: v })}>
              <SelectTrigger data-testid="disp-estatus-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DISP_EST.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDispModal(null)}>Cancelar</Button>
              <Button data-testid="disp-guardar-button" onClick={() => guardarDisp(false)} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Registrar</Button></div>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pagoModal} onOpenChange={(v) => !v && setPagoModal(null)}>
        <DialogContent className="max-w-sm" data-testid="pago-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Registrar pago realizado</DialogTitle></DialogHeader>
          {pagoModal && <div className="space-y-3">
            <Input data-testid="pago-fecha-input" type="date" value={pagoModal.fecha || ""} onChange={(e) => setPagoModal({ ...pagoModal, fecha: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="pago-capital-input" type="number" placeholder="Capital" value={pagoModal.capital || ""} onChange={(e) => setPagoModal({ ...pagoModal, capital: e.target.value })} />
              <Input type="number" placeholder="Interés" value={pagoModal.interes || ""} onChange={(e) => setPagoModal({ ...pagoModal, interes: e.target.value })} />
            </div>
            <Input placeholder="Nota / comprobante" value={pagoModal.nota || ""} onChange={(e) => setPagoModal({ ...pagoModal, nota: e.target.value })} />
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setPagoModal(null)}>Cancelar</Button>
              <Button data-testid="pago-guardar-button" onClick={registrarPago} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Registrar pago</Button></div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
